import React, { useContext, useEffect, useMemo, useState } from "react";
import CancelAndNextFooter from "../../../../../../common/CancelAndNextFooter";
import { Button, ButtonVariant, Menu, MenuItem } from "@merge-api/merge-javascript-shared";
import { FieldMappingModalStep, FieldMappingPathways } from "../../../../../../../models/Entities";
import RemoteFieldTypeaheadV2 from "./components/RemoteFieldTypeaheadV2";
import FieldMappingModalContext from "../context/FieldMappingModalContext";
import { ArrowRight, ChevronDown } from "lucide-react";
import FieldMappingModalBackButton from "../components/FieldMappingModalBackButton";
import FieldMappingModalTitle from "../components/FieldMappingModalTitle";
import {
  createFieldMappingOptions,
  getFieldMappingModalTitle,
  isAdvancedMappingAvailableForRemoteField,
} from "../helpers";
import clsx from "clsx";
import useAppContext from "../../../../../../context/useAppContext";
import usePartialFieldMappingOptions from "./hooks/usePartialFieldMappingOptions";

const RemoteFieldSelection = () => {
  const {
    linkedAccount,
    onModalClose,
    setModalStep,
    linkedAccountFieldMappingOptions,
    enabledCommonModels,
    selectedRemoteField,
    setSelectedRemoteField,
    selectedCommonModel,
    setSelectedCommonModel,
    selectedFieldMappingPathway,
    jsonAdvancedMappingExpression,
    setJsonAdvancedMappingExpression,
  } = useContext(FieldMappingModalContext);

  const [showAdvancedMappingOption, setShowAdvancedMappingOption] = useState(false);
  const availableFieldMappings = linkedAccountFieldMappingOptions?.remote_field_options;

  const { user } = useAppContext();
  const isJmesPathDeprecationEnabled = user?.is_jmes_path_deprecation_dashboard_enabled ?? false;
  const isFmoLoadingOptimizationPhase1Enabled =
    user?.is_fmo_loading_optimization_phase_1_enabled ?? false;

  const allOptions = useMemo(
    () => (availableFieldMappings ? createFieldMappingOptions(availableFieldMappings) : []),
    [availableFieldMappings],
  );

  const { partialOptionsByCommonModel } = usePartialFieldMappingOptions({
    linkedAccountId: linkedAccount?.id ?? "",
    selectedCommonModel,
    isCompleteResponseLoaded: availableFieldMappings != undefined,
    enabled: isFmoLoadingOptimizationPhase1Enabled,
  });
  const partialOptionsForSelectedModel =
    selectedCommonModel != null ? partialOptionsByCommonModel[selectedCommonModel] : undefined;

  const remoteFieldOptions = useMemo(() => {
    if (availableFieldMappings) {
      return isFmoLoadingOptimizationPhase1Enabled && selectedCommonModel
        ? allOptions.filter((opt) => opt.commonModelName === selectedCommonModel)
        : allOptions;
    }
    if (partialOptionsForSelectedModel) {
      return createFieldMappingOptions(partialOptionsForSelectedModel).filter(
        (opt) => opt.commonModelName === selectedCommonModel,
      );
    }
    return [];
  }, [
    availableFieldMappings,
    allOptions,
    isFmoLoadingOptimizationPhase1Enabled,
    selectedCommonModel,
    partialOptionsForSelectedModel,
  ]);

  const isLoadingRemoteFields =
    availableFieldMappings == undefined && partialOptionsForSelectedModel == undefined;

  const commonModelFilterOptions = enabledCommonModels;

  // calculate state if remote field already selected when component loads
  useEffect(() => {
    setShowAdvancedMappingOption(
      (selectedRemoteField?.type.includes("list") && selectedRemoteField.value?.length !== 0) ||
        selectedRemoteField?.type == "dict",
    );
  }, []);

  const onSelectCommonModel = (commonModel: string | null) => {
    setSelectedCommonModel(commonModel);
    setSelectedRemoteField!(null);
    setShowAdvancedMappingOption(false);
    setJsonAdvancedMappingExpression(null);
  };

  const getAndSetNextModalStep = (selectedFieldMappingPathway: FieldMappingPathways) => {
    switch (selectedFieldMappingPathway) {
      case FieldMappingPathways.LINKED_ACCOUNT_SPECIFIC_MAPPING:
        setModalStep(
          FieldMappingModalStep.COMMON_MODEL_SELECTION_AND_LINKED_ACCOUNT_MAPPING_CREATION,
        );
        break;
      case FieldMappingPathways.COMMON_MODEL_OVERRIDE:
        setModalStep(FieldMappingModalStep.COMMON_MODEL_OVERRIDE_SELECTION);
        break;
    }
  };

  return (
    <>
      <FieldMappingModalTitle
        title={getFieldMappingModalTitle(selectedFieldMappingPathway!, linkedAccount)}
        onClose={onModalClose}
      />
      <div className="px-6 mt-5">
        <FieldMappingModalBackButton
          destinationStep={FieldMappingModalStep.FIELD_MAPPING_OPTION_SELECTION}
          onBeforeNavigate={() => onSelectCommonModel(null)}
        />
        <div className="mb-4 mt-3.5">Select the third-party Remote Field you want to map</div>
        <div className="flex items-center gap-4">
          {isFmoLoadingOptimizationPhase1Enabled && (
            <Menu
              menuPlacement="bottom-start"
              menuClassName="max-h-[300px] overflow-y-auto"
              ButtonProps={{
                variant: ButtonVariant.TertiaryWhite,
                className: "group",
                rightIcon: (
                  <ChevronDown
                    size={16}
                    className="transition-transform duration-200 ease-in-out group-aria-expanded:rotate-180"
                  />
                ),
                children: selectedCommonModel
                  ? `Common Model: ${selectedCommonModel}`
                  : "All Common Models",
              }}
            >
              <MenuItem onClick={() => onSelectCommonModel(null)}>All Common Models</MenuItem>
              {commonModelFilterOptions.map((commonModel) => (
                <MenuItem key={commonModel} onClick={() => onSelectCommonModel(commonModel)}>
                  {commonModel}
                </MenuItem>
              ))}
            </Menu>
          )}
          <div className="min-w-0 flex-1">
            <RemoteFieldTypeaheadV2
              key={selectedCommonModel ?? "all-common-models"}
              value={selectedRemoteField}
              options={remoteFieldOptions}
              placeholder="Search Remote Fields by name or value..."
              matchCommonModel={selectedCommonModel == null}
              loading={isLoadingRemoteFields}
              onChange={(remoteField) => {
                if (remoteField) {
                  setSelectedRemoteField!(remoteField);
                  // reset jmes path on any remote field selection switch
                  setShowAdvancedMappingOption(false);
                  if (isAdvancedMappingAvailableForRemoteField(remoteField)) {
                    setTimeout(() => {
                      setShowAdvancedMappingOption(true);
                    }, 400);
                  }
                } else {
                  setSelectedRemoteField!(null);
                  // if advanced mapping tool is opened and closed and another remote field is selceted
                  setJsonAdvancedMappingExpression(null);
                }
              }}
            />
          </div>
        </div>
        <div
          className={`${
            (isJmesPathDeprecationEnabled || showAdvancedMappingOption) && selectedRemoteField
              ? `mt-4`
              : "h-0 opacity-0"
          } transition-all ease-in-out duration-400`}
        >
          {(isJmesPathDeprecationEnabled || showAdvancedMappingOption) && selectedRemoteField && (
            <div className={`${jsonAdvancedMappingExpression ? "bg-blue-0 rounded-md" : ""} `}>
              <div className="flex items-center justify-between">
                <div className="px-[14px] w-[500px]">
                  {jsonAdvancedMappingExpression && (
                    <div className="truncate">{jsonAdvancedMappingExpression}</div>
                  )}
                </div>
                <div
                  className={clsx("h-[40px] rounded-md items-center pt-2", {
                    "bg-blue-0": jsonAdvancedMappingExpression,
                    "bg-gray-0": !jsonAdvancedMappingExpression,
                  })}
                >
                  <Button
                    variant={ButtonVariant.TextBlue}
                    rightIcon={<ArrowRight size={12} />}
                    onClick={() => {
                      setModalStep(FieldMappingModalStep.ADVANCED_FIELD_MAPPING_SELECTION);
                    }}
                  >
                    <div className="mx-1">
                      {jsonAdvancedMappingExpression
                        ? "Edit advanced mapping"
                        : "Add advanced mapping"}
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mt-6">
          <CancelAndNextFooter
            isNextDisabled={selectedRemoteField == null}
            onCancel={onModalClose}
            onNext={() => {
              getAndSetNextModalStep(selectedFieldMappingPathway!);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default RemoteFieldSelection;
